/**
 * @fileoverview Access Request Page
 * @description Allows users to request access to modules they don't have,
 *              and view the status of their previous requests.
 * @module features/AccessRequest/pages/AccessRequestPage
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import { Send, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@/context';
import {
  fetchModules,
  fetchRoles,
  submitAccessRequest,
  fetchMyAccessRequests,
} from '@/services/api/rbac';
import { appInsights } from '@/telemetry';

const statusConfig = {
  pending: { color: 'warning', icon: Clock, label: 'Pending' },
  approved: { color: 'success', icon: CheckCircle, label: 'Approved' },
  rejected: { color: 'error', icon: XCircle, label: 'Rejected' },
};

const AccessRequestPage = () => {
  const { hasModuleGrant, isAdmin } = useAuth();
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [modulesData, rolesData, requestsData] = await Promise.all([
        fetchModules(),
        fetchRoles(),
        fetchMyAccessRequests(),
      ]);
      setModules(Array.isArray(modulesData) ? modulesData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setMyRequests(Array.isArray(requestsData) ? requestsData : []);
    } catch (err) {
      setError('Failed to load data');
      // Telemetry for data loading failure
      appInsights.trackException({ error: err });
    } finally {
      setLoading(false);
    }
  };

  // Filter modules to show only ones user doesn't already have access to
  const requestableModules = modules.filter(
    (m) => m.name !== 'admin_rbac' && !isAdmin && !hasModuleGrant(m.name)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedModule) {
      setError('Please select a module');
      // Telemetry for missing module selection
      appInsights.trackException({ error: new Error('Module not selected') });
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      // Telemetry for access request submission
      appInsights.trackEvent({ 
        name: 'AccessRequestSubmitted', 
        properties: { 
          module: selectedModule, 
          role: selectedRole ? selectedRole : undefined 
        } 
      });

      await submitAccessRequest({
        module_id: parseInt(selectedModule),
        requested_role_id: selectedRole ? parseInt(selectedRole) : undefined,
        reason,
      });
      setSuccess('Access request submitted successfully!');
      setSelectedModule('');
      setSelectedRole('');
      setReason('');
      // Refresh requests list
      const updated = await fetchMyAccessRequests();
      setMyRequests(Array.isArray(updated) ? updated : []);
    } catch (err) {
      setError(err.message || 'Failed to submit request');
      // Telemetry for access request submission failure
      appInsights.trackException({ error: err });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
        Request Module Access
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Request access to modules you need. An admin will review your request.
      </Typography>

      {/* Request Form */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          New Request
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Module"
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
            required
            fullWidth
          >
            {requestableModules.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.display_name} — {m.description}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Requested Role (optional)"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            fullWidth
            helperText="Select a role to request, or leave blank for default access"
          >
            <MenuItem value="">— No specific role —</MenuItem>
            {roles
              .filter((r) => r.name !== 'admin')
              .map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.display_name} — {r.description}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            label="Reason for request"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Explain why you need access to this module..."
          />

          <Button
            type="submit"
            variant="contained"
            startIcon={submitting ? <Spinner size={16} color="inherit" /> : <Send size={16} />}
            disabled={submitting || !selectedModule}
            sx={{ alignSelf: 'flex-start' }}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </Box>
      </Paper>

      {/* My Requests History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          My Requests
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {myRequests.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No access requests yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Module</TableCell>
                  <TableCell>Requested Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Review Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myRequests.map((req) => {
                  const cfg = statusConfig[req.status] || statusConfig.pending;
                  return (
                    <TableRow key={req.id}>
                      <TableCell>{req.module_display_name || req.module_name}</TableCell>
                      <TableCell>
                        {req.requested_role_display_name || req.requested_role_name || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cfg.label}
                          color={cfg.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {req.requested_at
                          ? new Date(req.requested_at).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell>{req.review_notes || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AccessRequestPage;
